import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CurrentUserProvider } from '@/hooks/useCurrentUser';
import CompaniesTab from '@/components/admin/finance/CompaniesTab';
import PaymentInstrumentsTab from '@/components/admin/finance/PaymentInstrumentsTab';
import type { Company, PaymentInstrument } from '@/components/admin/finance/types';


export default function FinancePage() {
    const navigate = useNavigate();
    const { t, isReady } = useI18n();
    const [activeTab, setActiveTab] = useState<'instruments' | 'companies'>('instruments');
    const [paymentInstruments, setPaymentInstruments] = useState<PaymentInstrument[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isReady) {
            fetchPaymentInstruments();
            fetchCompanies();
        }
    }, [isReady]);

    const fetchPaymentInstruments = async () => {
        try {
            setLoading(true);
            const response = await apiFetch('/api/payment-instruments/');
            if (response.ok) {
                const data = await response.json();
                setPaymentInstruments(data.payment_instruments || []);
            } else {
                setError(t('finance.paymentInstruments.loadFailed', 'admin'));
            }
        } catch (err) {
            setError(t('finance.paymentInstruments.loadFailed', 'admin'));
            console.error('Error fetching payment instruments:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const [companiesResponse, navCredentialsResponse] = await Promise.all([
                apiFetch('/api/companies/'),
                apiFetch('/api/nav-credentials/')
            ]);

            if (companiesResponse.ok) {
                const companiesData = await companiesResponse.json();

                // Get NAV credentials data if available
                let navCredentialsData = [];
                if (navCredentialsResponse.ok) {
                    navCredentialsData = await navCredentialsResponse.json();
                }

                // Merge NAV credentials status into companies
                const companiesWithCredentials = (Array.isArray(companiesData) ? companiesData : []).map((company: Company) => ({
                    ...company,
                    has_nav_credentials: navCredentialsData.some((cred: any) =>
                        cred.company_id === company.id && cred.is_active
                    )
                }));

                setCompanies(companiesWithCredentials);
            }
        } catch (err) {
            console.error('Error fetching companies:', err);
        }
    };


    const handleDeletePaymentInstrument = async (id: string) => {
        if (!confirm(t('finance.paymentInstruments.deleteConfirm', 'admin'))) {
            return;
        }

        try {
            const response = await apiFetch(`/api/payment-instruments/${id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                await fetchPaymentInstruments(); // Refresh the list

            } else {
                alert(t('finance.paymentInstruments.deleteFailed', 'admin'));
            }
        } catch (error) {
            console.error('Error deleting payment instrument:', error);
            alert(t('finance.paymentInstruments.deleteFailed', 'admin'));
        }
    };

    if (!isReady || loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg">{t('actions.loading', 'common')}</div>
                </div>
            </Layout>
        );
    }

    return (

        <CurrentUserProvider>
            <Layout>
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">
                                {t('finance.title', 'admin')}
                            </h1>
                            <p className="text-gray-600">
                                {t('finance.description', 'admin')}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'instruments' | 'companies')}>
                            <TabsList>
                                <TabsTrigger value="instruments">{t('finance.paymentInstruments.tab', 'admin')}</TabsTrigger>
                                <TabsTrigger value="companies">{t('finance.companies.tab', 'admin')}</TabsTrigger>
                            </TabsList>
                        </Tabs>

                        {activeTab === 'instruments' ? (
                            <PaymentInstrumentsTab
                                instruments={paymentInstruments}
                                loading={loading}
                                error={error}
                                onDelete={handleDeletePaymentInstrument}
                                onCreated={fetchPaymentInstruments}
                            />
                        ) : (
                            <CompaniesTab
                                companies={companies}
                                loading={loading}
                                onSelect={(id) => navigate({ to: `/admin/finance/companies/${id}` })}
                                onCreated={fetchCompanies}
                            />
                        )}
                    </div>
                </div>
            </Layout>
        </CurrentUserProvider>
    );
}

export const Route = createFileRoute('/admin/finance/')({
    component: FinancePage,
})
